import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Shield, Download, FileText, Loader2, AlertTriangle, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DARK_LOGO = "https://customer-assets.emergentagent.com/job_vaptara-vuln-ai/artifacts/znyb36lz_vaptara_logo_w.png";

export default function SharedReportPage() {
  const { shareToken } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await axios.get(`${API}/reports/shared/${shareToken}`);
        setReport(res.data);
      } catch (err) {
        setError(err.response?.status === 410 ? 'This share link has expired.' : 'Report not found or unavailable.');
      } finally { setLoading(false); }
    };
    if (shareToken) fetchReport();
  }, [shareToken]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/reports/shared/${shareToken}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vaptara_shared_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {}
    finally { setDownloading(false); }
  };

  const reportTypeLabels = { executive: 'Executive Summary', technical: 'Technical Assessment', compliance: 'Compliance Report' };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020817] flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-10 w-10 text-blue-500 mx-auto mb-3 animate-pulse" />
          <Loader2 className="h-5 w-5 animate-spin mx-auto text-blue-500" />
          <p className="text-sm text-slate-400 mt-2">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#020817] flex items-center justify-center">
        <Card className="max-w-md w-full bg-[#0f172a] border-slate-800 text-white">
          <CardContent className="flex flex-col items-center py-12 px-8">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mb-4" />
            <h2 className="text-lg font-bold mb-2">Report Unavailable</h2>
            <p className="text-sm text-slate-400 text-center">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020817] text-slate-50" data-testid="shared-report-page">
      {/* Header */}
      <header className="border-b border-slate-800 bg-[#020817]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={DARK_LOGO} alt="Vaptara" className="h-8" />
            <Badge variant="outline" className="border-blue-500/30 text-blue-400 text-xs">Shared Report</Badge>
          </div>
          <Button onClick={downloadPdf} disabled={downloading} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="shared-download-pdf-btn">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download PDF
          </Button>
        </div>
      </header>

      {/* Report Content */}
      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            data-testid="shared-report-title">
            {reportTypeLabels[report.report_type] || 'Security Report'} - {report.project_name}
          </h1>
          <div className="flex items-center gap-3 mt-2 text-sm text-slate-400">
            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{new Date(report.generated_at).toLocaleString()}</span>
            <Badge variant="outline" className="capitalize text-xs border-slate-700">{report.report_type}</Badge>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#0f172a] border-slate-800" data-testid="shared-total-findings">
            <CardContent className="p-5 text-center">
              <p className="text-3xl font-bold">{report.summary?.total_vulnerabilities}</p>
              <p className="text-xs text-slate-400 mt-1">Total Findings</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0f172a] border-slate-800" data-testid="shared-critical">
            <CardContent className="p-5 text-center">
              <p className="text-3xl font-bold text-red-500">{report.summary?.severity_distribution?.critical || 0}</p>
              <p className="text-xs text-slate-400 mt-1">Critical</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0f172a] border-slate-800" data-testid="shared-high">
            <CardContent className="p-5 text-center">
              <p className="text-3xl font-bold text-orange-500">{report.summary?.severity_distribution?.high || 0}</p>
              <p className="text-xs text-slate-400 mt-1">High</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0f172a] border-slate-800" data-testid="shared-risk-score">
            <CardContent className="p-5 text-center">
              <p className="text-3xl font-bold text-blue-500">{report.summary?.risk_score}</p>
              <p className="text-xs text-slate-400 mt-1">Risk Score</p>
            </CardContent>
          </Card>
        </div>

        {/* Status Distribution */}
        {report.summary?.status_distribution && (
          <Card className="bg-[#0f172a] border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Status Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(report.summary.status_distribution).map(([status, count]) => (
                  <div key={status} className="p-3 rounded-lg bg-slate-800/50 text-center">
                    <p className="text-xl font-bold">{count}</p>
                    <p className="text-xs text-slate-400 capitalize">{status.replace('_', ' ')}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Compliance */}
        {report.compliance_alignment?.length > 0 && (
          <Card className="bg-[#0f172a] border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Compliance Alignment</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {report.compliance_alignment.map(fw => (
                  <Badge key={fw} variant="outline" className="border-blue-500/30 text-blue-400">{fw}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Findings */}
        {report.findings?.length > 0 && (
          <Card className="bg-[#0f172a] border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Findings ({report.findings.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-800">
                {report.findings.map((f, i) => (
                  <div key={f.vuln_id || i} className="px-6 py-3" data-testid={`shared-finding-${i}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className={`text-xs ${
                        f.severity === 'critical' ? 'border-red-500/30 text-red-500' :
                        f.severity === 'high' ? 'border-orange-500/30 text-orange-500' :
                        f.severity === 'medium' ? 'border-yellow-500/30 text-yellow-500' :
                        'border-green-500/30 text-green-500'
                      }`}>{f.severity}</Badge>
                      <span className="text-sm font-medium">{f.title}</span>
                      <span className="text-xs text-slate-500 font-mono ml-auto">{f.cvss_score} CVSS</span>
                    </div>
                    <p className="text-xs text-slate-400">{f.description}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span className="font-mono">{f.cwe_id}</span>
                      <span>{f.asset}</span>
                      <span className="capitalize">{f.status?.replace('_', ' ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center py-6 text-xs text-slate-500">
          <Separator className="mb-4 bg-slate-800" />
          Generated by Vaptara AI Security Platform | Confidential
        </div>
      </main>
    </div>
  );
}
